import React, { Component } from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import {
  InputGroup,
  Col,
  Row,
  Form,
  ControlLabel,
  FormGroup,
  FormControl,
  Alert,
  Button,
  Checkbox,
} from 'react-bootstrap';
import Time from '../../components/Time/Time';
import LoadingButton from '../../components/LoadingButton/LoadingButton';
import { withTranslation } from 'react-i18next';
import {
  MaterialType,
  WeighedMaterialType,
} from '../../constants/MaterialModels';

class InventoryAdjust extends Component {
  constructor(props) {
    super(props);
    this.state = this.getInitialState();
    this.anchor = React.createRef();
  }

  getInitialState() {
    return {
      adjustedQuantity: 0,
      binId: '',
      reason: '',
      comment: '',
      error: null,
      saving: false,
      clearOtherLocationInventory: false,
    };
  }

  componentDidMount () {
    this.anchor.current.scrollIntoView();
    this.mounted = true;
    const { materialId, materialStore } = this.props;
    const material = materialStore.getMaterialByItemId(materialId);

    if (material?.assignment === MaterialType.Weighed) {
      const materialDetails = materialStore.materialDetails[material?.materialDetailId];

      if (
        materialDetails?.type === WeighedMaterialType.CoarseAggregate
        ||
        materialDetails?.type === WeighedMaterialType.FineAggregate
      ) {
        this.setState({ clearOtherLocationInventory: true });
      }
    }
  }

  componentWillUnmount () {
    this.mounted = false;
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.materialId &&
      nextProps.materialId !== this.props.materialId
    ) {
      this.setState({
        adjustedQuantity: this.props.materialStore.getMaterialByItemId(
          nextProps.materialId
        ).currentBalance,
      });
    }
  }

  onChange = field => evt => {
    this.setState({ [field]: evt.target.value });
  };

  onAdjust = () => {
    const { materialId, materialStore, notifierStore, t } = this.props;
    const { adjustedQuantity, reason, comment, binId, clearOtherLocationInventory } = this.state;
    const material = materialStore.getMaterialByItemId(materialId, true);
    const currentBalance = binId ? materialStore.getBinBalance(material, binId) : material.currentBalance;
    const { reasonCode } = materialStore;

    if (material.assignment === MaterialType.Weighed && !binId) {
      this.setState({
        saving: false,
        error: t('This field is required!'),
      });
      return;
    }

    this.setState({
      saving: true,
      error: '',
    });

    materialStore
      .adjustQuantity(materialId, {
        itemId: materialId,
        binIds: material.assignment === MaterialType.Weighed ? [ binId ] : [],
        quantity: adjustedQuantity - currentBalance, // sends over only added up amount
        reason: reasonCode.find(v => v.id === Number(reason)),
        clearOtherLocationInventory,
        comment,
      })
      .then(() => {
        notifierStore.showInfo({ key: 'Material inventory updated!', props: { materialCode: material.code }});
        if (this.mounted) {
          this.setState({
            ...this.getInitialState(),
          });
        }
      })
      .catch(err => {
        if (this.mounted) {
          this.setState({
            error: err.message,
            saving: false,
          });
        }
      });
  };

  onClose = () => {
    this.setState({
      adjustedQuantity: 0,
      reason: '',
      comment: '',
      error: null,
      saving: false,
    });
    this.props.onClose();
  };

  renderForm() {
    const { materialId, devicesStore, t } = this.props;
    const { saving, adjustedQuantity, reason, comment, binId, error, clearOtherLocationInventory } = this.state;
    const { materialStore } = this.props;
    const material = materialStore.getMaterialByItemId(materialId) || {};
    const { reasonCode } = materialStore;
    const materialBins = [];

    if (material.bins) {
      material.bins.forEach(v => {
        const bin = devicesStore.getDeviceById(v.binId);
        if (bin) {
          const binBalance = material.deviceBalance.find(x => x.deviceId === v.binId);
          materialBins.push({ ...bin, ...binBalance });
        }
        const linkedBin = devicesStore.getDeviceById(v.linkedBinId);
        if (linkedBin) {
          const binBalance = material.deviceBalance.find(x => x.deviceId === v.linkedBinId);
          materialBins.push({ ...linkedBin, ...binBalance });
        }
      });
    }

    return (
      <Form onSubmit={e => e.preventDefault()}>
        <Row className="form-fields">
          <Col sm={3}>
            <ControlLabel>
              {t('New Quantity')}
            </ControlLabel>
            <FormGroup>
              <InputGroup>
                <FormControl
                  name="adjusted_quantity"
                  type="number"
                  value={adjustedQuantity}
                  onChange={this.onChange('adjustedQuantity')}
                />
                <InputGroup.Addon>
                  {material?.itemInventoryUnitSymbol}
                </InputGroup.Addon>
              </InputGroup>
            </FormGroup>
          </Col>
          <Col sm={3}>
            <FormGroup>
              <ControlLabel>{t('Reason')}</ControlLabel>
              <FormControl
                name="reason"
                componentClass="select"
                value={reason}
                onChange={this.onChange('reason')}
                bsSize="sm"
              >
                <option value=""/>
                {reasonCode.map((option, i) => (
                  <option key={i} value={option.id}>
                    {option.shortDescription} {option.description}
                  </option>
                ))}
              </FormControl>
            </FormGroup>
          </Col>
          {material.assignment === MaterialType.Weighed && (
            <Col sm={3}>
              <ControlLabel>
                {t('Bin')}
              </ControlLabel>
              <FormGroup className={!binId && error ? 'has-error' : ''}>
                <FormControl
                  name="binId"
                  componentClass="select"
                  value={binId}
                  onChange={this.onChange('binId')}
                  bsSize="sm"
                >
                  <option/>
                  {materialBins.map((option, i) => (
                    <option key={i} value={option.id}>
                      {option.description || option.id} :: {option.currentBalance}
                    </option>
                  ))}
                </FormControl>
              </FormGroup>
            </Col>
          )}
          {material.assignment === MaterialType.Weighed && (
            <Col sm={3}>
              <ControlLabel>
                {t('Clear Other Bins Inventory')}
              </ControlLabel>
              <Checkbox
                inline
                name={'clearOtherLocationInventory'}
                value={clearOtherLocationInventory}
                checked={!!clearOtherLocationInventory}
                onChange={e => this.setState({ 'clearOtherLocationInventory': e.target.checked })}
              />
            </Col>
          )}
          <Col sm={12}>
            <FormGroup>
              <ControlLabel>{t('Comment')}</ControlLabel>
              <FormControl
                name="comment"
                componentClass="textarea"
                type="text"
                value={comment}
                onChange={this.onChange('comment')}
              />
            </FormGroup>
          </Col>
          <Col sm={12}>
            {!!error && <Alert bsStyle="danger">{error}</Alert>}
          </Col>
        </Row>
        <Row className="form-buttons">
          <Col sm={12} className="text-right">
            <Button onClick={this.onClose}>{t('Close')}</Button>
            {' '}
            <LoadingButton
              bsStyle="primary"
              onClick={this.onAdjust}
              label={t('Save')}
              loadingLabel={t('Saving')}
              isLoading={saving}
            />
          </Col>
        </Row>
      </Form>
    );
  }

  render() {
    const { materialId, materialStore, t } = this.props;
    const material = materialStore.getMaterialByItemId(materialId);
    const name = material ? materialStore.getMaterialName(material) : '';

    return !materialId ? '' : (
      <div className="material-inventory-adjust" ref={this.anchor}>
        <Row className="form-header">
          <Col sm={12}>
            <a className="btn-panel-close" onClick={this.onClose}>x</a>
            <h4>{t('Inventory adjustment')} - {name}</h4>
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <p>
              <b>{t('Current Quantity')}: </b>
              {material && materialStore.getDevicesBalance(material)} {' '}
              {material && material.itemInventoryUnitSymbol}
              <br/>
              <b>{t('Time')}: </b> <Time />
            </p>
          </Col>
        </Row>
        {this.renderForm()}
      </div>
    );
  }
}

export default withTranslation()(inject('materialStore', 'notifierStore', 'devicesStore')(observer(InventoryAdjust)));
